import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, orderBy, onSnapshot, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { RefreshCw, Search, Calendar, Clock, User, ArrowLeftRight, Check, X, MapPin, Briefcase } from 'lucide-react';
import { sendNotification, NOTIFICATION_TYPES } from '../utils/notificationUtils';
import { logAuditEvent } from '../utils/auditLog';
import { isManager } from '../utils/permissions';
import { parseLocalDate } from '../utils/schedulingEngine';

export default function SwapBoard({ user, userData, companyId }) {
  const { t } = useTranslation();
  const [openShifts, setOpenShifts] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [myShifts, setMyShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('open'); // open, posted, approvals, my-requests
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [postReason, setPostReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const userIsManager = isManager(userData);

  const getRequesterId = (request) => request?.requesterId || request?.requestedBy || null;
  const getRequesterName = (request) => request?.requesterName || request?.requestedByName || 'Unknown';
  const formatShiftDate = (date, options) => parseLocalDate(date).toLocaleDateString(undefined, options);
  const formatRequestDate = (dateValue, options) => {
    if (!dateValue) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return parseLocalDate(dateValue).toLocaleDateString(undefined, options);
    }
    return new Date(dateValue).toLocaleDateString(undefined, options);
  };

  useEffect(() => {
    if (!companyId) return;

    // Open/unassigned shifts — simplified query, filter client-side to avoid
    // Firestore SDK watch-stream assertion crash with in + inequality combos
    const openQ = query(
      collection(db, 'companies', companyId, 'shifts'),
      where('status', 'in', ['open', 'unassigned']),
      orderBy('date'),
      orderBy('startTime')
    );
    const unsub1 = onSnapshot(openQ, snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setOpenShifts(all.filter(s => s.date >= today));
      setLoading(false);
    }, (err) => { console.error('SwapBoard openShifts query error:', err); setLoading(false); });

    const requestsQ = query(
      collection(db, 'companies', companyId, 'swapRequests'),
      orderBy('createdAt', 'desc')
    );
    const unsub2 = onSnapshot(requestsQ, snap => {
      setAllRequests(snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          requesterId: data.requesterId || data.requestedBy || null,
          requesterName: data.requesterName || data.requestedByName || 'Unknown',
          shiftStartTime: data.shiftStartTime || data.shiftStart || '',
          shiftEndTime: data.shiftEndTime || data.shiftEnd || '',
        };
      }));
    }, (err) => console.error('SwapBoard requests query error:', err));

    // My shifts (for posting) — filter date client-side
    if (user?.uid) {
      const myQ = query(
        collection(db, 'companies', companyId, 'shifts'),
        where('assignedStaffId', '==', user.uid),
        orderBy('date')
      );
      const unsub3 = onSnapshot(myQ, snap => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setMyShifts(all.filter(s => s.date >= today));
      }, (err) => console.error('SwapBoard myShifts query error:', err));

      return () => { unsub1(); unsub2(); unsub3(); };
    }
    return () => { unsub1(); unsub2(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, user?.uid]);

  const filteredOpenShifts = useMemo(() => {
    let list = openShifts;
    if (dateFilter) list = list.filter(s => s.date === dateFilter);
    if (roleFilter) list = list.filter(s => (s.requiredRole || '').toLowerCase().includes(roleFilter.toLowerCase()));
    if (searchTerm) list = list.filter(s =>
      (s.groupName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.requiredRole || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.notes || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    return list;
  }, [openShifts, dateFilter, roleFilter, searchTerm]);

  const swapPosts = useMemo(() => (
    allRequests.filter((request) => (
      request?.status === 'open' &&
      (request?.type === 'posted' || (!request?.type && !!getRequesterId(request) && !!request?.shiftDate))
    ))
  ), [allRequests]);

  const allSwapRequests = useMemo(() => (
    allRequests.filter((request) => getRequesterId(request) === user?.uid)
  ), [allRequests, user?.uid]);

  const pendingApprovalRequests = useMemo(() => (
    userIsManager ? allRequests.filter((request) => request.status === 'pending') : []
  ), [allRequests, userIsManager]);

  const filteredSwapPosts = useMemo(() => {
    let list = swapPosts.filter(s => getRequesterId(s) !== user?.uid); // Don't show own posts
    if (searchTerm) list = list.filter(s =>
      getRequesterName(s).toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.reason || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    return list;
  }, [swapPosts, searchTerm, user?.uid]);

  const handlePickUp = async (shift) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'companies', companyId, 'swapRequests'), {
        type: 'pickup',
        shiftId: shift.id,
        shiftDate: shift.date,
        shiftStartTime: shift.startTime,
        shiftEndTime: shift.endTime,
        groupName: shift.groupName || '',
        requesterId: user.uid,
        requesterName: userData?.displayName || user.email,
        status: 'pending',
        createdAt: new Date().toISOString()
      });
      await logAuditEvent(companyId, 'swap_requested', {
        performedBy: user.uid,
        metadata: {
          action: 'pickup_request',
          shiftId: shift.id,
          date: shift.date
        }
      });
      alert(t('swapBoard.pickupRequested', 'Pickup request submitted!'));
    } catch (err) {
      alert('Error: ' + err.message);
    }
    setSubmitting(false);
  };

  const handlePostForSwap = async () => {
    if (!selectedShift || submitting) return;
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'companies', companyId, 'swapRequests'), {
        type: 'posted',
        shiftId: selectedShift.id,
        shiftDate: selectedShift.date,
        shiftStartTime: selectedShift.startTime,
        shiftEndTime: selectedShift.endTime,
        groupName: selectedShift.groupName || '',
        requesterId: user.uid,
        requesterName: userData?.displayName || user.email,
        reason: postReason,
        status: 'open',
        createdAt: new Date().toISOString()
      });
      await logAuditEvent(companyId, 'swap_requested', {
        performedBy: user.uid,
        metadata: {
          shiftId: selectedShift.id,
          date: selectedShift.date,
          reason: postReason
        }
      });
      setShowPostModal(false);
      setSelectedShift(null);
      setPostReason('');
      alert(t('swapBoard.posted', 'Shift posted for swap!'));
    } catch (err) {
      alert('Error: ' + err.message);
    }
    setSubmitting(false);
  };

  const handleOfferSwap = async (swapPost) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'companies', companyId, 'swapRequests'), {
        type: 'swap_offer',
        originalSwapId: swapPost.id,
        shiftId: swapPost.shiftId,
        shiftDate: swapPost.shiftDate,
        requesterId: user.uid,
        requesterName: userData?.displayName || user.email,
        originalPosterId: swapPost.requesterId,
        originalPosterName: swapPost.requesterName,
        status: 'pending',
        createdAt: new Date().toISOString()
      });
      await sendNotification(companyId, swapPost.requesterId, NOTIFICATION_TYPES.SWAP_REQUEST, {
        title: 'New swap offer',
        message: `${userData?.displayName || user.email} wants to swap shifts with you`,
        swapRequestId: swapPost.id
      });
      alert(t('swapBoard.offerSent', 'Swap offer sent!'));
    } catch (err) {
      alert('Error: ' + err.message);
    }
    setSubmitting(false);
  };

  const handleApproveReject = async (requestId, action) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const request = allRequests.find(r => r.id === requestId);

      await updateDoc(doc(db, 'companies', companyId, 'swapRequests', requestId), {
        status: action,
        resolvedAt: new Date().toISOString(),
        resolvedBy: user.uid
      });

      if (action === 'approved' && request?.shiftId) {
        const nextStaffId = request.requesterId || request.requestedBy || null;
        const nextStaffName = request.requesterName || request.requestedByName || '';

        if (nextStaffId) {
          await updateDoc(doc(db, 'companies', companyId, 'shifts', request.shiftId), {
            assignedStaffId: nextStaffId,
            assignedStaffName: nextStaffName,
            assignedTo: nextStaffId,
            assignedToName: nextStaffName,
            status: 'assigned',
            updatedAt: new Date().toISOString(),
          });
        }

        if (request.originalSwapId) {
          await updateDoc(doc(db, 'companies', companyId, 'swapRequests', request.originalSwapId), {
            status: 'approved',
            resolvedAt: new Date().toISOString(),
            resolvedBy: user.uid,
          });
        }
      }

      const requesterId = getRequesterId(request);
      if (requesterId) {
        await sendNotification(companyId, requesterId, action === 'approved' ? NOTIFICATION_TYPES.SWAP_APPROVED : NOTIFICATION_TYPES.SWAP_REJECTED, {
          title: `Swap ${action}`,
          message: `Your swap request has been ${action}`,
          swapRequestId: requestId
        });
      }
      await logAuditEvent(companyId, action === 'approved' ? 'swap_approved' : 'swap_rejected', {
        performedBy: user.uid,
        targetId: requestId,
        targetType: 'swap'
      });
    } catch (err) {
      alert('Error: ' + err.message);
    }
    setSubmitting(false);
  };

  const tabs = [
    { id: 'open', label: t('swapBoard.openShifts', 'Open Shifts'), count: filteredOpenShifts.length },
    { id: 'posted', label: t('swapBoard.swapBoard', 'Swap Board'), count: filteredSwapPosts.length },
    ...(userIsManager ? [{ id: 'approvals', label: t('swapBoard.approvals', 'Approvals'), count: pendingApprovalRequests.length }] : []),
    { id: 'my-requests', label: t('swapBoard.myRequests', 'My Requests'), count: allSwapRequests.length }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <ArrowLeftRight className="text-primary" size={24} />
          {t('swapBoard.title', 'Shift Swap Board')}
        </h1>
        <button onClick={() => setShowPostModal(true)} className="bg-primary hover:bg-primary/80 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
          {t('swapBoard.postShift', 'Post Shift for Swap')}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white/5 rounded-xl p-1 border border-white/10">
        {tabs.map(tb => (
          <button key={tb.id} onClick={() => setTab(tb.id)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition ${tab === tb.id ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'}`}>
            {tb.label}
            {tb.count > 0 && <span className="ml-1.5 text-xs bg-white/20 px-1.5 py-0.5 rounded-full">{tb.count}</span>}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder={t('swapBoard.search', 'Search shifts...')}
            className="w-full pl-10 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent" />
        </div>
        {tab === 'open' && (
          <>
            <input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-primary" />
            <input type="text" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
              placeholder={t('swapBoard.filterRole', 'Filter by role...')}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 focus:ring-2 focus:ring-primary" />
          </>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Open Shifts Tab */}
          {tab === 'open' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredOpenShifts.length === 0 ? (
                <div className="col-span-full text-center py-12 text-gray-400">
                  <Calendar size={32} className="mx-auto mb-2 opacity-50" />
                  <p>{t('swapBoard.noOpen', 'No open shifts available.')}</p>
                </div>
              ) : filteredOpenShifts.map(shift => (
                <div key={shift.id} className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-primary/30 transition group">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-sm font-semibold text-white">
                        {formatShiftDate(shift.date, { weekday: 'short', month: 'short', day: 'numeric' })}
                      </p>
                      <p className="text-primary font-mono text-sm">{shift.startTime} – {shift.endTime}</p>
                    </div>
                    <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">{t('swapBoard.open', 'Open')}</span>
                  </div>
                  {shift.groupName && (
                    <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                      <MapPin size={12} /> {shift.groupName}
                    </div>
                  )}
                  {shift.requiredRole && (
                    <div className="flex items-center gap-1 text-xs text-gray-400 mb-2">
                      <Briefcase size={12} /> {shift.requiredRole}
                    </div>
                  )}
                  {shift.notes && <p className="text-xs text-gray-400 mb-3">{shift.notes}</p>}
                  <button onClick={() => handlePickUp(shift)} disabled={submitting}
                    className="w-full py-2 bg-primary/20 hover:bg-primary/40 text-primary rounded-lg text-sm font-medium transition disabled:opacity-50">
                    {t('swapBoard.pickUp', 'Request to Pick Up')}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Swap Board Tab */}
          {tab === 'posted' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filteredSwapPosts.length === 0 ? (
                <div className="col-span-full text-center py-12 text-gray-400">
                  <RefreshCw size={32} className="mx-auto mb-2 opacity-50" />
                  <p>{t('swapBoard.noSwapPosts', 'No shifts posted for swap.')}</p>
                </div>
              ) : filteredSwapPosts.map(post => (
                <div key={post.id} className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-primary/30 transition">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                      <User size={18} className="text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{getRequesterName(post)}</p>
                      <p className="text-xs text-gray-400">{t('swapBoard.wantsToSwap', 'Wants to swap')}</p>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 mb-3">
                    <p className="text-sm text-white">
                      <Calendar size={12} className="inline mr-1" />
                      {formatShiftDate(post.shiftDate, { weekday: 'short', month: 'short', day: 'numeric' })}
                    </p>
                    <p className="text-sm text-primary mt-0.5">
                      <Clock size={12} className="inline mr-1" />
                      {post.shiftStartTime} – {post.shiftEndTime}
                    </p>
                    {post.groupName && <p className="text-xs text-gray-400 mt-0.5"><MapPin size={10} className="inline mr-1" />{post.groupName}</p>}
                  </div>
                  {post.reason && <p className="text-xs text-gray-400 italic mb-3">"{post.reason}"</p>}
                  <button onClick={() => handleOfferSwap(post)} disabled={submitting}
                    className="w-full py-2 bg-amber-500/20 hover:bg-amber-500/40 text-amber-400 rounded-lg text-sm font-medium transition disabled:opacity-50">
                    {t('swapBoard.offerSwap', 'Offer to Swap')}
                  </button>
                </div>
              ))}
            </div>
          )}

          {tab === 'approvals' && userIsManager && (
            <div className="space-y-3">
              {pendingApprovalRequests.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Check size={32} className="mx-auto mb-2 opacity-50" />
                  <p>{t('swapBoard.noApprovals', 'No pending approvals.')}</p>
                </div>
              ) : pendingApprovalRequests.map(req => (
                <div key={req.id} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-500/20 text-amber-400">
                        {req.type === 'pickup'
                          ? t('swapBoard.pickup', 'Pickup')
                          : req.type === 'swap_offer'
                            ? t('swapBoard.swapOfferLabel', 'Swap Offer')
                            : t('swapBoard.postedLabel', 'Posted')}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatRequestDate(req.shiftDate || req.createdAt, { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    <p className="text-sm text-white mt-1">{req.requesterName}</p>
                    <p className="text-sm text-gray-300 mt-1">
                      {req.shiftStartTime && `${req.shiftStartTime} - ${req.shiftEndTime}`}
                      {req.groupName && ` · ${req.groupName}`}
                    </p>
                    {req.reason && <p className="text-xs text-gray-400 mt-1">{req.reason}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleApproveReject(req.id, 'approved')}
                      disabled={submitting}
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 text-sm font-medium disabled:opacity-50"
                    >
                      <Check size={14} /> {t('swap.approve')}
                    </button>
                    <button
                      onClick={() => handleApproveReject(req.id, 'rejected')}
                      disabled={submitting}
                      className="flex items-center gap-1 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 text-sm font-medium disabled:opacity-50"
                    >
                      <X size={14} /> {t('swap.reject')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* My Requests Tab */}
          {tab === 'my-requests' && (
            <div className="space-y-3">
              {allSwapRequests.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <ArrowLeftRight size={32} className="mx-auto mb-2 opacity-50" />
                  <p>{t('swapBoard.noMyRequests', 'No swap requests yet.')}</p>
                </div>
              ) : allSwapRequests.map(req => (
                <div key={req.id} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        req.type === 'pickup' ? 'bg-blue-500/20 text-blue-400' :
                        req.type === 'posted' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-purple-500/20 text-purple-400'
                      }`}>
                        {req.type === 'pickup' ? t('swapBoard.pickup') : req.type === 'posted' ? t('swapBoard.postedLabel') : t('swapBoard.swapOfferLabel')}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatRequestDate(req.shiftDate || req.createdAt, { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    <p className="text-sm text-white mt-1">
                      {req.shiftStartTime && `${req.shiftStartTime} – ${req.shiftEndTime}`}
                      {req.groupName && ` · ${req.groupName}`}
                    </p>
                    {req.reason && <p className="text-xs text-gray-400 mt-0.5">{req.reason}</p>}
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                    req.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                    req.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                    req.status === 'open' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-amber-500/20 text-amber-400'
                  }`}>
                    {t(`swap.status${req.status.charAt(0).toUpperCase() + req.status.slice(1)}`)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Post Shift Modal */}
      {showPostModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-white/10 rounded-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-white mb-4">{t('swapBoard.postShift', 'Post Shift for Swap')}</h2>
            {myShifts.length === 0 ? (
              <p className="text-gray-400 text-sm">{t('swapBoard.noShiftsToPost', 'You have no upcoming shifts to post.')}</p>
            ) : (
              <>
                <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
                  {myShifts.map(shift => (
                    <label key={shift.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition ${selectedShift?.id === shift.id ? 'bg-primary/20 border border-primary/40' : 'bg-white/5 border border-white/10 hover:border-white/20'}`}>
                      <input type="radio" name="swapShift" checked={selectedShift?.id === shift.id}
                        onChange={() => setSelectedShift(shift)} className="accent-primary" />
                      <div>
                        <p className="text-sm text-white">
                          {formatShiftDate(shift.date, { weekday: 'short', month: 'short', day: 'numeric' })}
                        </p>
                        <p className="text-xs text-gray-400">{shift.startTime} – {shift.endTime} {shift.groupName && `(${shift.groupName})`}</p>
                      </div>
                    </label>
                  ))}
                </div>
                <textarea value={postReason} onChange={e => setPostReason(e.target.value)}
                  placeholder={t('swapBoard.reasonPlaceholder', 'Reason for swap (optional)...')}
                  rows={2}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 mb-4 focus:ring-2 focus:ring-primary" />
              </>
            )}
            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowPostModal(false); setSelectedShift(null); setPostReason(''); }}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white transition">
                {t('common.cancel', 'Cancel')}
              </button>
              <button onClick={handlePostForSwap} disabled={!selectedShift || submitting}
                className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium disabled:opacity-50 transition hover:bg-primary/80">
                {t('swapBoard.post', 'Post')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
